import { Chip, useTheme } from '@mui/material';
import { Palette, styled, type Theme } from '@mui/material/styles';
import { Spacing } from '@mui/system';

declare module '@mui/material/Chip' {
  interface ChipPropsSizeOverrides {
    sm: true;
    md: true;
    lg: true;
    medium: false;
    small: false;
  }

  interface ChipPropsColorOverrides {
    primary: true;
    secondary: true;
    error: true;
    warning: true;
    success: true;
    info: false;
    default: false;
    orange: true;
    grayBlue: true;
    blueLight: true;
    purple: true;
    indigo: true;
    rose: true;
  }
}

const getSizeMapping = (spacing: Spacing, typography: Theme['typography']) => ({
  sm: {
    paddingX: spacing(2),
    fontSize: typography.textXs.fontSize,
    padding: spacing(0.75, 0),
    height: 24,
  },
  md: {
    paddingX: spacing(2.5),
    fontSize: typography.textSm.fontSize,
    padding: spacing(1, 0),
    height: 28,
  },
  lg: {
    paddingX: spacing(3),
    fontSize: typography.textLg.fontSize,
    padding: spacing(1.25, 0),
    height: 34,
  },
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getColorMapping = (palette: Palette): any => ({
  primary: {
    backgroundColor: palette.primary[50],
    color: palette.primary[700],
    borderColor: palette.primary![600],
  },
  secondary: {
    backgroundColor: palette.grey[50],
    color: palette.grey[700],
    borderColor: palette.grey![600],
  },
  error: {
    backgroundColor: palette.error[50],
    color: palette.error[600],
    borderColor: palette.error[600],
  },
  warning: {
    backgroundColor: palette.warning[50],
    color: palette.warning[600],
    borderColor: palette.warning![600],
  },
  success: {
    backgroundColor: palette.success[100],
    color: palette.success[700],
    borderColor: palette.success[600],
  },
  orange: {
    backgroundColor: palette.orange![50],
    color: palette.orange![700],
    borderColor: palette.orange![600],
  },
  purple: {
    backgroundColor: palette.purple![50],
    color: palette.purple![700],
    borderColor: palette.purple![600],
  },
  grayBlue: {
    backgroundColor: palette.grayBlue![50],
    color: palette.grayBlue![700],
    borderColor: palette.grayBlue![600],
  },
  blue: {
    backgroundColor: palette.blue![50],
    color: palette.blue![700],
    borderColor: palette.blue?.main,
  },
  blueLight: {
    backgroundColor: palette.blueLight![50],
    color: palette.blueLight![700],
    borderColor: palette.blueLight![600],
  },
  indigo: {
    backgroundColor: palette.indigo![50],
    color: palette.indigo![700],
    borderColor: palette.indigo![600],
  },
  rose: {
    backgroundColor: palette.rose![100],
    color: palette.rose![800],
    borderColor: palette.rose![700],
  },
});

export const StyledChip = styled(Chip)(({
  size = 'md' as const,
  color = 'primary' as const,
  variant = 'filled' as const,
}) => {
  const { palette, spacing, typography } = useTheme();
  const sizeMapping = getSizeMapping(spacing, typography)[size];
  const colorMappings = getColorMapping(palette);
  // Fallback a primary si el color no está soportado (mismo criterio que
  // category-chip), para no reventar el render ante un color desconocido.
  const colorMapping = colorMappings[color] ?? colorMappings.primary;

  return {
    borderRadius: variant === 'outlined' ? spacing(4) : spacing(2),
    borderColor: colorMapping.borderColor,
    paddingLeft: sizeMapping.paddingX,
    paddingRight: sizeMapping.paddingX,
    height: sizeMapping.height,
    backgroundColor: colorMapping.backgroundColor,
    '& .MuiChip-label': {
      color: colorMapping.color,
      fontWeight: 500,
      fontSize: sizeMapping.fontSize,
    },
    '& .MuiChip-icon': {
      fontSize: 16,
      color: colorMapping.borderColor,
    },
    '& .MuiChip-deleteIcon': {
      fontSize: sizeMapping.fontSize,
      color: colorMapping.borderColor,
    },
  };
});
